<script setup lang="ts">
import { computed } from 'vue';

import useResponsive from '@/utils/responsive-util';
import { useUserStore } from '@/store/userStore';
import { Locales } from '@/store/language.interface';
import { getLabel } from '@/utils/data-display-utils';

const userStore = useUserStore();

// CONST
const { isMobile } = useResponsive();

// PROPS
const props = defineProps<{
  isAdmin?: boolean;
  preLaunchState?: {
    name: string;
    icon: string;
    quantity: number;
  };
  ongoingState: {
    name: string;
    icon: string;
    quantity: number;
  };
  completedState?: {
    name: string;
    icon: string;
    quantity: number;
  };
  archivedState?: {
    name: string;
    icon: string;
    quantity: number;
  };
  languageEnabled?: Locales[];
}>();

// MODEL
const model = defineModel();

// COMPUTED
const showIcon = computed(() => {
  return !(isMobile.value && userStore.isAdmin());
});
</script>

<template>
  <div class="base-button-toggle">
    <q-btn-toggle
      v-model="model"
      no-caps
      unelevated
      toggle-text-color="selected"
      class="full-width-toggle"
      :options="[
        ...(props.isAdmin ? [{ value: 'preLaunchValue', slot: 'preLaunch' }] : []),
        { value: 'ongoingValue', slot: 'ongoing' },
        { value: 'completedValue', slot: 'completed' },
        ...(props.isAdmin ? [{ value: 'archivedValue', slot: 'archived' }] : []),
      ]"
    >
      <template v-slot:preLaunch>
        <q-icon v-if="!isMobile" :name="preLaunchState.icon" />
        <span :class="isMobile ? 'flexDirection' : 'spaceBetween'">
          <span>{{ getLabel(preLaunchState.name, props.languageEnabled) }}</span>
          <span>[{{ preLaunchState.quantity }}]</span>
        </span>
      </template>

      <template v-slot:ongoing>
        <q-icon v-if="showIcon" :name="ongoingState.icon" />
        <span :class="isMobile ? 'flexDirection' : 'spaceBetween'">
          <span class="text">{{ getLabel(ongoingState.name, props.languageEnabled) }}</span>
          <span>[{{ ongoingState.quantity }}]</span>
        </span>
      </template>

      <template v-slot:completed>
        <q-icon v-if="showIcon" :name="completedState.icon" />
        <span :class="isMobile ? 'flexDirection' : 'spaceBetween'">
          <span>{{ getLabel(completedState.name, props.languageEnabled) }}</span>
          <span>[{{ completedState.quantity }}]</span>
        </span>
      </template>

      <template v-slot:archived>
        <q-icon v-if="!isMobile" :name="archivedState.icon" />
        <span :class="isMobile ? 'flexDirection' : 'spaceBetween'">
          <span>{{ getLabel(archivedState.name, props.languageEnabled) }}</span>
          <span>[{{ archivedState.quantity }}]</span>
        </span>
      </template>
    </q-btn-toggle>
  </div>
</template>

<style lang="scss">
.base-button-toggle {
  .q-icon {
    margin-right: 8px;
  }

  .flexDirection {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .spaceBetween {
    display: flex;
    align-items: center;
    gap: 5px;

    .text {
      white-space: nowrap;
    }
  }

  @media screen and (max-width: $breakpoint-md) {
    .q-btn {
      width: 100%;
    }

    .full-width-toggle {
      width: 100%;
    }
  }

  .q-btn-group {
    border-radius: 6px;
    background-color: $color-neutral-white;

    .text-selected {
      font-weight: bold;
    }
  }

  .bg-primary {
    background-color: $color-primary-12 !important;
    color: $color-primary-darken;
    border: solid 1px $color-primary-darken;
  }
  @media screen and (max-width: $breakpoint-sm) {
    .q-btn {
      padding: $space-sm;
    }

    .full-width-toggle {
      width: 100%;
    }
  }
}
</style>
